import { DatePicker, Field, Input } from '@olytic/foundation';
import { useEffect, useState } from 'react';

const toNumber = (value: string): number | null => (value.trim() === '' ? null : Number(value));

/** A number input that saves on blur or Enter, and only when the value changed. */
export function NumberSetting({
  label,
  value,
  onSave,
  help,
  disabled,
}: {
  label: string;
  value: number | null;
  onSave: (next: number | null) => void;
  help?: string;
  disabled?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  useEffect(() => setDraft(value === null ? '' : String(value)), [value]);
  const commit = () => {
    const next = toNumber(draft);
    if (next !== value && !Number.isNaN(next)) onSave(next);
  };
  return (
    <Field label={label} {...(help ? { help } : {})}>
      <Input
        inputMode="decimal"
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
      />
    </Field>
  );
}

/** A date (YYYY-MM-DD) that saves when picked or on blur, and only when it changed; blank saves null. */
export function DateSetting({
  label,
  value,
  onSave,
  help,
}: {
  label: string;
  value: string | null;
  onSave: (next: string | null) => void;
  help?: string;
}): JSX.Element {
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  const commit = (next: string) => {
    const saved = next.trim() === '' ? null : next.trim();
    if (saved !== value) onSave(saved);
  };
  return (
    <Field label={label} {...(help ? { help } : {})}>
      <DatePicker value={draft} onChange={setDraft} onBlur={() => commit(draft)} onPick={commit} />
    </Field>
  );
}

/** One row of the totals card. */
export function Total({ label, value, strong }: { label: string; value: string; strong?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-sm">
      <span className="text-[var(--fg-muted)]">{label}</span>
      <span className={`tabular-nums ${strong ? 'font-strong text-[var(--fg-strong)]' : 'text-[var(--fg)]'}`}>{value}</span>
    </div>
  );
}

